/*eslint-disable */
import React, { useState, useContext, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'

import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Textarea from 'components/Textarea'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import MultiSelectionDropdown from 'components/MultiSelectDropdown'
import Input from 'components/Input'
import { isValidDecimalNumber, noZeroAfterDecimal } from 'utils/isValidDecimalNumber'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import { delay } from 'utils/delay'
import { SiteContext } from 'providers/site'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import { reasonDefaultOptions, reasonOptionsIrreversibleSelected, reasonOptionsReversibleSelected, IrreversibleIndex, ReversibleIndex } from 'constants/rejectTriggerReason'

// Create criterion E-specific options for mutual exclusivity
const reasonQualifyOptions = [
  { label: 'Qualifies', value: 1, disabled: false },
  { label: 'Improved site information', value: 2, disabled: true },
  { label: 'KBA criteria revision', value: 3, disabled: true },
  { label: 'Revision to Red List status', value: 4, disabled: true },
  { label: 'Revision of global threshold', value: 5, disabled: true },
  { label: 'No longer geographically restricted', value: 6, disabled: true },
  { label: 'Genuine decline, irreversible', value: 7, disabled: true },
  { label: 'Genuine decline, reversible', value: 8, disabled: true },
  { label: 'Genuine decline, other factors', value: 9, disabled: true }
]

const reasonNonQualifyOptions = [
  { label: 'Qualifies', value: 1, disabled: true },
  { label: 'Improved site information', value: 2, disabled: false },
  { label: 'KBA criteria revision', value: 3, disabled: false },
  { label: 'Revision to Red List status', value: 4, disabled: false },
  { label: 'Revision of global threshold', value: 5, disabled: false },
  { label: 'No longer geographically restricted', value: 6, disabled: false },
  { label: 'Genuine decline, irreversible', value: 7, disabled: false },
  { label: 'Genuine decline, reversible', value: 8, disabled: false },
  { label: 'Genuine decline, other factors', value: 9, disabled: false }
]
import Checkbox from 'components/Checkbox'
import { EcosystemContentFooter } from '../EcosystemData/style'
import Button from 'components/Button'
import CheckIcon from 'assets/icons/check-icon'
import { AssessmentPageSubTittleIconWrapper, WrapperCheckBox } from '../../../Assessment/style'
import { requestCreateCriterionE } from 'api/criterionE'
import CriterionEDocsUploader from '../SupportingDocs/CriterionEDocsUploader'

const tooltipXOffset = 0
const tooltipYOffset = 16
const inputSaveDelayTime = 300
const updateIrreplaceabilityScoreTimeout = delay(inputSaveDelayTime)
const updateDateOfAssesmentTimeOut = delay(inputSaveDelayTime)
const updateIsCheckedTimeOut = delay(inputSaveDelayTime)
const updateTriggerReasonIdTimeOut = delay(inputSaveDelayTime)
const updateTriggerReasonTextTimeOut = delay(inputSaveDelayTime)
const YEAR_INPUT_LENGTH = 4

const accordionContent = ({ site, item: criterionE, updateCriterionE, siteId, toggleAccordion, defaultPage = false, criterionEs = [] }) => {
  const params = useParams()
  const checkedValue = useRef([])
  const { siteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const criterionEId = criterionE?.site_irreplaceability_id || criterionE?.site_irreplaceability_history_id
  const checkFields = (siteProposalFieldValidation?.criterionE && criterionEId && siteProposalFieldValidation.criterionE[criterionEId])
    ? siteProposalFieldValidation.criterionE[criterionEId]
    : defaultSiteProposalValidationResults.criterionE
  
  const [isChecked, setIsChecked] = useState(criterionE?.irreplaceability_assessment_approved || false)
  const [irreplaceabilityScore, setIrreplaceabilityScore] = useState(criterionE?.irreplaceability_score || '')
  const [checkedOptions, setCheckedOptions] = useState(reasonDefaultOptions)
  const [qualificationReason, setQualificationReason] = useState((criterionE?.trigger_reason_id && Array.isArray(criterionE.trigger_reason_id)) ? criterionE.trigger_reason_id.map((item) => {
    if (Number(item) > 3) {
      return Number(item) - 1
    }
    return Number(item)
  }) : [])

  const qualified = (criterionE?.trigger_reason_id && Array.isArray(criterionE.trigger_reason_id)) ? criterionE.trigger_reason_id.some((element) => {
    // eslint-disable-next-line no-magic-numbers
    return Number(element) === 1
  }) || criterionE.trigger_reason_id.length === 0 : true

  const [reassessmentRational, setReassessmentRational] = useState(criterionE?.trigger_reason_text || '')
  const [assessmentDate, setAssessmentDate] = useState(criterionE?.year || '')
  const disabled = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID) || !qualified
  const disabledQualificationReason = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID)

  // On component mount, extract only the year from the full date saved in the database
  useEffect(() => {
    try {
      if (criterionE?.year) {
        const dateObj = new Date(criterionE.year)
        if (!isNaN(dateObj.getTime())) {
          const savedYear = dateObj.getFullYear().toString()
          setAssessmentDate(savedYear)
        }
      }

      if (criterionE?.irreplaceability_score !== undefined && criterionE.irreplaceability_score !== null && criterionE.irreplaceability_score !== '') {
        // Check if the score is a whole number, if so, show it without decimals
        const score = Number(criterionE.irreplaceability_score)
        if (!isNaN(score)) {
          // eslint-disable-next-line no-magic-numbers
          if (score % 1 === 0) {
            setIrreplaceabilityScore(score.toString()) // Show whole number without decimal places
          } else {
            // eslint-disable-next-line no-magic-numbers
            setIrreplaceabilityScore(score.toFixed(2)) // Show decimal number up to 2 places
          }
        }
      }
    } catch (error) {
      console.error('Error initializing criterion E form:', error)
    }
  }, [criterionE?.year, criterionE?.irreplaceability_score])

  async function handleChangeValueChecked(Ids) {
    checkedValue.current = Ids
    const qualifyIndex = 0 // "Qualifies" is at index 0
    const isAnyNonQualifyOptionsSelected = Ids.some((item) => {
      return item.checked && item.value !== 1
    })

    if (Ids[IrreversibleIndex].checked) {
      setCheckedOptions(reasonOptionsIrreversibleSelected)
    } else if (Ids[ReversibleIndex].checked) {
      setCheckedOptions(reasonOptionsReversibleSelected)
    } else if (Ids[qualifyIndex].checked) {
      setCheckedOptions(reasonQualifyOptions)
    } else if (isAnyNonQualifyOptionsSelected) {
      setCheckedOptions(reasonNonQualifyOptions)
    } else {
      setCheckedOptions(reasonDefaultOptions)
    }
  }

  async function handleChangeQualificationReason(e) {
    try {
      if (!Array.isArray(e)) {
        return
      }
      setQualificationReason(e)

      const formattedReasons = e.map((item) => {
        if (item > 3) {
          return item + 1
        }
        return item
      })

      const triggerReasonParams = formattedReasons.includes(1) ? { trigger_reason_id: formattedReasons } : { trigger_reason_id: formattedReasons, irreplaceability_assessment_approved: false }

      if (criterionE?.site_irreplaceability_id && !criterionE?.site_irreplaceability_history_id) {
        return await createAndUpdate({ ...triggerReasonParams })
      }

      updateTriggerReasonIdTimeOut(async () => {
        try {
          await updateCriterionE({ criterionEId: criterionE.site_irreplaceability_id || criterionE.site_irreplaceability_history_id, ...triggerReasonParams })
        } catch (error) {
          console.error('Error updating trigger reason:', error)
        }
      })

      if (formattedReasons.some(num => num > 1)) {
        setIsChecked(false)
      }
    } catch (error) {
      console.error('Error handling qualification reason change:', error)
    }
  }

  async function handleChangeReassessmentRational(e) {
    try {
      setReassessmentRational(e.target.value)

      if (criterionE?.site_irreplaceability_id && !criterionE?.site_irreplaceability_history_id) {
        return await createAndUpdate({ trigger_reason_text: e.target.value })
      }

      updateTriggerReasonTextTimeOut(async () => {
        try {
          await updateCriterionE({ criterionEId: criterionE.site_irreplaceability_id || criterionE.site_irreplaceability_history_id, trigger_reason_text: e.target.value })
        } catch (error) {
          console.error('Error updating reassessment rational:', error)
        }
      })
    } catch (error) {
      console.error('Error handling reassessment rational change:', error)
    }
  }

  async function createAndUpdate(element) {
    try {
      const createdCriterionE = await requestCreateCriterionE(siteId, params.siteHistoryId)
      if (createdCriterionE && (createdCriterionE.site_irreplaceability_id || createdCriterionE.site_irreplaceability_history_id)) {
        await updateCriterionE({ criterionEId: createdCriterionE.site_irreplaceability_id || createdCriterionE.site_irreplaceability_history_id, ...element })
      }
    } catch (error) {
      console.error('Error creating/updating criterion E:', error)
      // Don't throw - allow user to continue
    }
  }

  const handleCheckboxIrreplaceabilityChange = async () => {
    try {
      setIsChecked((state) => !state)

      if (!criterionE?.site_irreplaceability_id && !criterionE?.site_irreplaceability_history_id) {
        return await createAndUpdate({ irreplaceability_assessment_approved: !isChecked })
      }
      updateIsCheckedTimeOut(async () => {
        try {
          await updateCriterionE({ criterionEId: criterionE.site_irreplaceability_id || criterionE.site_irreplaceability_history_id, irreplaceability_assessment_approved: !isChecked })
        } catch (error) {
          console.error('Error updating checkbox:', error)
        }
      })
    } catch (error) {
      console.error('Error handling checkbox change:', error)
    }
  }

  const handleChangeIrreplaceabilityScore = async (e) => {
    try {
      const value = e.target.value

      // Check if the value is a valid decimal number
      if (isValidDecimalNumber(Number(value))) {
        // If the value is a whole number (no decimal places), accept it
        // eslint-disable-next-line no-magic-numbers
        if (Number(value) % 1 === 0) {
          setIrreplaceabilityScore(value)
          if (!criterionE?.site_irreplaceability_id && !criterionE?.site_irreplaceability_history_id) {
            return await createAndUpdate({ irreplaceability_score: Number(value) || null })
          }
          updateIrreplaceabilityScoreTimeout(async () => {
            try {
              await updateCriterionE({ criterionEId: criterionE.site_irreplaceability_id || criterionE.site_irreplaceability_history_id, irreplaceability_score: Number(value) || null })
            } catch (error) {
              console.error('Error updating irreplaceability score:', error)
            }
          })
        }
        // If the value has decimal places, ensure there are no trailing zeros after the decimal
        else if (noZeroAfterDecimal(value)) {
          setIrreplaceabilityScore(value)
          // eslint-disable-next-line no-magic-numbers
          const formattedValue = Number(parseFloat(value).toFixed(10)) // Save with 10 decimal places precision
          if (!criterionE?.site_irreplaceability_id && !criterionE?.site_irreplaceability_history_id) {
            return await createAndUpdate({ irreplaceability_score: formattedValue || null })
          }
          updateIrreplaceabilityScoreTimeout(async () => {
            try {
              await updateCriterionE({ criterionEId: criterionE.site_irreplaceability_id || criterionE.site_irreplaceability_history_id, irreplaceability_score: formattedValue || null })
            } catch (error) {
              console.error('Error updating irreplaceability score:', error)
            }
          })
        }
      }
    } catch (error) {
      console.error('Error handling irreplaceability score change:', error)
    }
  }

  const handleChangeAssessmentDate = async (e) => {
    try {
      const yearInput = e.target.value

      // Limit input to a 4-digit year
      if (new RegExp(`^\\d{0,${YEAR_INPUT_LENGTH}}$`).test(yearInput)) {
        setAssessmentDate(yearInput)
        // Check if the input is a valid 4-digit year
        if (yearInput.length === YEAR_INPUT_LENGTH || yearInput === '') {
          // Prevent saving future years
          const currentYear = new Date().getFullYear()
          if (yearInput && Number(yearInput) > currentYear) {
            return
          }
          const fullDate = yearInput ? `${yearInput}-01-01` : null
          if (!criterionE?.site_irreplaceability_id && !criterionE?.site_irreplaceability_history_id) {
            return await createAndUpdate({ year: fullDate })
          }
          updateDateOfAssesmentTimeOut(async () => {
            try {
              // Save as full date (YYYY-01-01)
              await updateCriterionE({ criterionEId: criterionE.site_irreplaceability_id || criterionE.site_irreplaceability_history_id, year: fullDate })
            } catch (error) {
              console.error('Error updating assessment date:', error)
            }
          })
        }
      }
    } catch (error) {
      console.error('Error handling assessment date change:', error)
    }
  }

  return (
    <Container width='100%' padding='0px'>
      <div data-test="irreplaceability">
        <Row>
          <Col md={12}>
            <WrapperCheckBox disabled={disabled}>
              <Checkbox
                label={<FormattedMessage id="checkboxIrreplaceability" />}
                onChange={handleCheckboxIrreplaceabilityChange}
                checked={isChecked}
                field="checkboxIrreplaceability"
                disabled={disabled}
                dataTest="irreplaceability-checkbox"
                error={
                  checkFields?.irreplaceability_assessment_approved
                    ? {
                      field: '',
                      error: ''
                    }
                    : { field: 'checkboxIrreplaceability', error: 'pleaseCompleteThisField' }
                }
              />
              <Tooltip
                style={{ zIndex: 1000 }}
                placement="bottom"
                trigger={['hover']}
                align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                overlay={{ label: 'irreplaceabilityCheckboxLabel', infoTooltip: 'irreplaceabilityCheckboxHint' }}
                type="big"
              >
                <AssessmentPageSubTittleIconWrapper>
                  <InfoIcon />
                </AssessmentPageSubTittleIconWrapper>
              </Tooltip>
            </WrapperCheckBox>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Input
              dataTestId="irreplaceability-score-input"
              value={irreplaceabilityScore}
              onChange={handleChangeIrreplaceabilityScore}
              infoTooltip="irreplaceabilityScoreHint"
              placeholder="enterIrreplaceabilityScore"
              label="irreplaceabilityScore"
              type='decimal'
              field="irreplaceabilityScore"
              disabled={site.SiteStatusId !== SITE_STATUS_DRAFT_ID || !isChecked}
              error={
                checkFields?.irreplaceability_score
                  ? {
                    field: '',
                    error: ''
                  }
                  : { field: 'irreplaceabilityScore', error: 'pleaseCompleteThisField' }
              }
            />
          </Col>
          <Col md={6}>
            <Input
              value={assessmentDate}
              onChange={handleChangeAssessmentDate}
              infoTooltip="irreplaceabilityAssessmentDateHint"
              placeholder="enterAssessmentDate"
              label="irreplaceabilityAssessmentDateLabel"
              field="irreplaceabilityAssessmentDate"
              type='number'
              disabled={site.SiteStatusId !== SITE_STATUS_DRAFT_ID || !isChecked}
              error={
                checkFields?.year
                  ? {
                    field: '',
                    error: ''
                  }
                  : { field: 'irreplaceabilityAssessmentDate', error: 'pleaseCompleteThisField' }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <CriterionEDocsUploader site={site} criterionEs={criterionEs} criterionE={criterionE} />
          </Col>
        </Row>
        <Row>
          {(criterionE?.last_reassessment_date !== null && site.reassessmentFlag && !defaultPage) && (
            <Col md={6}>
              <MultiSelectionDropdown
                value={qualificationReason}
                saveChanges={handleChangeQualificationReason}
                valueChecked={handleChangeValueChecked}
                label='triggerEcosystemLabel'
                infoTooltip='triggerLabelHint'
                field='qualificationReason'
                error={qualificationReason?.length === 0 ? {
                  field: 'qualificationReason',
                  error: 'pleaseCompleteThisField'
                } : { field: '', error: '' }}
                secondStepOptions={checkedOptions}
                placeholder='criterionCTriggerPlaceholder'
              />
            </Col>
          )}
          {(criterionE.last_reassessment_date !== null && site.reassessmentFlag && !defaultPage) && (
            <Col md={6}>
              <Textarea
                value={reassessmentRational}
                onChange={handleChangeReassessmentRational}
                placeholder='justificationNoChangeCriterionCPlaceholder'
                label='justificationNoChange'
                infoTooltip='qualificationReasonHint'
                field='rationalForReassessment'
                height='128px'
                disabled={disabledQualificationReason}
              />
            </Col>
          )}
        </Row>

        {(criterionE?.last_reassessment_date && site.SiteStatusId === SITE_STATUS_DRAFT_ID && !defaultPage) && (
          <Row>
            <Col md={12}>
              <EcosystemContentFooter>
                <Button height='40px' isDisabled={!(criterionE?.trigger_reason_id && Array.isArray(criterionE.trigger_reason_id) && criterionE.trigger_reason_id.length > 0)} onClick={toggleAccordion}
                  type={(criterionE?.trigger_reason_id && Array.isArray(criterionE.trigger_reason_id) && criterionE.trigger_reason_id.length > 0) ? 'primary' : 'disabledPrimary'}>
                  <CheckIcon />
                  <FormattedMessage id='finishEditing' />
                </Button>
              </EcosystemContentFooter>
            </Col>
          </Row>
        )}
        {(!criterionE?.last_reassessment_date && site.SiteStatusId === SITE_STATUS_DRAFT_ID && !defaultPage) && (
          <Row>
            <Col md={12}>
              <EcosystemContentFooter>
                <Button height='40px' onClick={toggleAccordion}
                  type='primary'>
                  <CheckIcon />
                  <FormattedMessage id='finishEditing' />
                </Button>
              </EcosystemContentFooter>
            </Col>
          </Row>
        )}
      </div>
    </Container>
  )
}

export default accordionContent
